import { DashboardMetrics } from "@campaign/shared-types";
import { useEffect, useState } from "react";
import { Alert, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getDashboard } from "../services/api";

export default function Dashboard() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchDashboard() {
      try {
        setLoading(true);
        const data = await getDashboard();
        setMetrics(data);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching dashboard:", err);
        setError(err.message || "Failed to load dashboard metrics");
      } finally {
        setLoading(false);
      }
    }

    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading dashboard...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Dashboard</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (!metrics) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">No dashboard data available</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Dashboard</h2>

      <Row>
        <Col md={4} className="mb-3">
          <Card>
            <Card.Body>
              <h4>Assignments!</h4>
              <p>
                Visit the <Link to="/assignments">Assignments</Link> page to see
                your assignments.
              </p>
            </Card.Body>
          </Card>
        </Col>
        {metrics.peopleContacted !== undefined && (
          <Col md={4} className="mb-3">
            <Card>
              <Card.Body>
                <h6 className="text-muted mb-1">People Contacted</h6>
                <h2 className="mb-0">
                  {metrics.peopleContacted.toLocaleString()}
                </h2>
              </Card.Body>
            </Card>
          </Col>
        )}
        {metrics.projectedVotes !== undefined && (
          <Col md={4} className="mb-3">
            <Card>
              <Card.Body>
                <h6 className="text-muted mb-1">Projected Votes</h6>
                <h2 className="mb-0">
                  {metrics.projectedVotes.toLocaleString()}
                </h2>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* School zones - currently empty, will be populated with actual data later */}
      {(metrics.schoolZones.elementary.length > 0 ||
        metrics.schoolZones.middle.length > 0 ||
        metrics.schoolZones.high.length > 0) && (
        <Card className="mt-4">
          <Card.Header>
            <h5>School Zone Distribution</h5>
            <small className="text-muted">
              Number of D8 addresses in each school zone
            </small>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <h6>Elementary</h6>
                {metrics.schoolZones.elementary.length > 0 ? (
                  <ul className="list-unstyled">
                    {metrics.schoolZones.elementary.map((zone) => (
                      <li key={zone.name}>
                        {zone.name}: {zone.count.toLocaleString()}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted">No data available</p>
                )}
              </Col>
              <Col md={4}>
                <h6>Middle</h6>
                {metrics.schoolZones.middle.length > 0 ? (
                  <ul className="list-unstyled">
                    {metrics.schoolZones.middle.map((zone) => (
                      <li key={zone.name}>
                        {zone.name}: {zone.count.toLocaleString()}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted">No data available</p>
                )}
              </Col>
              <Col md={4}>
                <h6>High</h6>
                {metrics.schoolZones.high.length > 0 ? (
                  <ul className="list-unstyled">
                    {metrics.schoolZones.high.map((zone) => (
                      <li key={zone.name}>
                        {zone.name}: {zone.count.toLocaleString()}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted">No data available</p>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <div className="text-center mt-4 text-muted">
        <small>
          Last updated: {new Date(metrics.lastUpdated).toLocaleString()}
        </small>
      </div>
    </Container>
  );
}
